import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import React, { FC, useEffect, useRef, useState } from 'react';

import LottieIcon from '../LottieIcon';
import { IconPlaceholder } from './IconPlaceholder';

type tAnimationStatus = 'loading' | 'loaded' | 'error';

interface IAnimatedIcon {
  animationUrl: string;
  loop?: boolean;
  autoPlay?: boolean;
  width?: string;
  height?: string;
  title?: string;
  className?: string;
  /** Fires once on permanent load/parse failure; aborts (url change, unmount) do not. */
  onError?: () => void;
}

const AnimatedIcon: FC<IAnimatedIcon> = ({
  animationUrl,
  loop = true,
  autoPlay = true,
  width,
  height,
  title,
  className,
  onError
}) => {
  const [animationData, setAnimationData] = useState<Record<string, unknown> | null>(
    null
  );
  const [status, setStatus] = useState<tAnimationStatus>('loading');

  // Ref, not a fetch dep: call sites pass inline arrows that would re-trigger the request.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!animationUrl) {
      setAnimationData(null);
      setStatus('error');
      onErrorRef.current?.();
      return undefined;
    }

    const controller = new AbortController();
    setAnimationData(null);
    setStatus('loading');

    const load = async (): Promise<void> => {
      try {
        // Plain `fetch`, deliberately: `extensions/axiosHelper.js` mutates the DEFAULT
        // axios instance with `withCredentials` + an X-CSRF-TOKEN header, forcing a CORS
        // preflight that makes `Access-Control-Allow-Origin: *` CDN responses unusable.
        // Keep this credential-free and header-free so it stays a CORS-simple request.
        const response = await fetch(animationUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(
            `Animation request failed with status ${response.status} ${response.statusText}`
          );
        }

        const payload: unknown = await response.json();
        const animation = parseAnimatedImage(payload);
        if (!animation) {
          throw new Error('Animation JSON payload was empty or of an unexpected type');
        }

        if (controller.signal.aborted) return;
        setAnimationData(animation);
        setStatus('loaded');
      } catch (error) {
        if (controller.signal.aborted) return;

        captureMediaLoadError(error, {
          kind: 'image',
          src: animationUrl,
          context: 'animated-icon'
        });
        setStatus('error');
        onErrorRef.current?.();
      }
    };

    load();

    return () => controller.abort();
  }, [animationUrl]);

  // Never render nothing: a sized placeholder keeps the tile from reflowing.
  if (status !== 'loaded' || !animationData) {
    return (
      <IconPlaceholder
        width={width}
        height={height}
        className={className}
        loading={status === 'loading'}
      />
    );
  }

  return (
    <LottieIcon
      animationJSONData={animationData}
      height={height}
      width={width}
      loop={loop}
      autoPlay={autoPlay}
      title={title}
      className={className}
    />
  );
};

export default AnimatedIcon;

function parseAnimatedImage(animation: unknown): Record<string, unknown> | null {
  if (typeof animation === 'string') {
    try {
      const parsed: unknown = JSON.parse(animation);
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof animation === 'object' && animation !== null) {
    return animation as Record<string, unknown>;
  }

  return null;
}
